import React, { Fragment, useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import Helmet from 'react-helmet';
import classNames from 'classnames';
import { graphql, useQuery } from 'react-apollo';
import compose from 'lodash.flowright';

import withModal from '../../../../../../components/global/modal/withModal';
import DismissButton from '../../../../../../components/global/DismissButton';
import getCardCategoryCardGroupsQuery from '../../../../graphql/queries/getCardCategoryCardGroups';
import getCardGroupCardsQuery from '../../../../graphql/queries/getCardGroupCards';
import CardGroupCardSelect from './CardGroupCardSelect';
import useClickOutside from '../../../../../../hooks/useClickOutside';
import NativeActivityIndicator from '../../../../../../components/global/NativeActivityIndicator';
import { getDefaultCardSideIds } from '../../helpers';
import useVirtualPageView from '../../../../../../apps/pro/services/analytics/useVirtualPageView';

const propTypes = {
  className: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  cardCategory: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    card_groups: PropTypes.shape({
      total: PropTypes.number.isRequired,
      records: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string.isRequired,
          name: PropTypes.string.isRequired,
        }).isRequired,
      ).isRequired,
    }).isRequired,
  }),
  onCardSelect: PropTypes.func.isRequired,
  hasAccountCard: PropTypes.bool,
};

const CardSelect = ({
  className,
  onClose,
  cardCategory,
  onCardSelect,
  hasAccountCard,
}) => {
  const classes = classNames({
    'card-select': true,
    [className]: !!className,
  });
  const ref = useRef();
  useClickOutside(ref, () => {
    onClose();
  });
  const { onVirtualPageViewTrack } = useVirtualPageView({
    fireOnMount: false,
  });

  const [selectedCardGroup, setSelectedCardGroup] = useState(null);
  const [loadingCardSelection, setLoadingCardSelection] = useState(null);
  const { data: { cardCategories, loading: loadingCardGroup } = {} } = useQuery(
    getCardGroupCardsQuery,
    {
      variables: {
        card_category_id: cardCategory?.id,
        card_group_id: selectedCardGroup?.id,
      },
      skip: !selectedCardGroup?.id || !cardCategory,
      notifyOnNetworkStatusChange: true,
    },
  );

  const cardGroups = cardCategories?.records?.[0]?.card_groups;

  const loading = !cardCategory || loadingCardGroup || !cardGroups;
  const categoryCardGroups = cardCategory?.card_groups?.records ?? [];

  useEffect(() => {
    // When the CardCategory first loads select its first CardGroup by default.
    const [defaultCardGroup] = categoryCardGroups;
    if (defaultCardGroup) {
      setSelectedCardGroup(defaultCardGroup);
    }
  }, [categoryCardGroups]);

  // If there's only one card in the selected CardGroup
  // skip the modal and auto-select it. This solves
  // the "Just Because" category which is just one Card
  // without any custom designed cards.
  useEffect(() => {
    if (cardGroups?.records?.length > 0) {
      const cards = cardGroups.records[0].cards.records;

      if (cards.length === 1 && categoryCardGroups.length === 1) {
        const [card] = cards;
        const defaultCardSideIds = getDefaultCardSideIds(card);
        onCardSelect({
          card_id: card.id,
          front_card_side_id: defaultCardSideIds.front_card_side_id,
          back_card_side_id: defaultCardSideIds.back_card_side_id,
        });
        onClose(true);
      }
    }
  }, [cardGroups]);

  useEffect(() => {
    if (selectedCardGroup) {
      onVirtualPageViewTrack({
        pageViewTitle: `Create a card - ${cardCategory.name} - Choose ${selectedCardGroup.name} design`,
        pageViewPath: `/categories/${cardCategory.name}/${selectedCardGroup.name}`,
      });
    }
  }, [selectedCardGroup]);

  const bodyClasses = [
    ...new Set((document?.body?.className ?? '').split(' ')),
  ].join(' ');

  return (
    <div className={classes} ref={ref}>
      <Helmet>
        <body
          className={classNames({
            // Retain existing body classes
            [bodyClasses]: true,
            'body-card-select-modal-open': true,
            'body-card-select-modal-open-account-card': hasAccountCard,
          })}
        />
      </Helmet>

      <div className="card-element-control-header-dismiss">
        <DismissButton onClick={() => onClose()} />
      </div>
      <div className="card-element-control-header">
        <h3 className="card-element-control-header-title">Select a template</h3>
      </div>

      {categoryCardGroups?.length > 1 && (
        <div className="card-select-card-group-nav">
          {categoryCardGroups.map(categoryCardGroup => {
            return (
              <button
                key={categoryCardGroup.id}
                onClick={() => setSelectedCardGroup(categoryCardGroup)}
                className={classNames({
                  'card-select-card-group-nav-item': true,
                  'is-active': selectedCardGroup?.id === categoryCardGroup.id,
                })}
              >
                {categoryCardGroup.name}
              </button>
            );
          })}
        </div>
      )}

      <div className="card-select-body">
        {loading ? (
          <div className="text-center">
            <NativeActivityIndicator />
          </div>
        ) : (
          <Fragment>
            <div className="card-select-list">
              <CardGroupCardSelect
                key={selectedCardGroup?.id}
                cardGroup={cardGroups?.records?.[0]}
                onCardSelect={async input => {
                  setLoadingCardSelection({ id: input.card_id });
                  await onCardSelect(input);
                  setLoadingCardSelection(null);
                }}
                loadingCard={loadingCardSelection}
              />
            </div>
          </Fragment>
        )}
      </div>
    </div>
  );
};

CardSelect.propTypes = propTypes;

const CARD_SELECT_MODAL = 'CARD_SELECT_MODAL';

const withCardCategoryCardGroups = graphql(getCardCategoryCardGroupsQuery, {
  options: ({ cardCategoryId }) => {
    return {
      variables: {
        card_category_id: cardCategoryId,
      },
    };
  },
  props: ({ data: { cardCategories } }) => ({
    cardCategory: cardCategories?.records?.[0],
  }),
});

export default withModal(compose(withCardCategoryCardGroups)(CardSelect), {
  name: CARD_SELECT_MODAL,
  dismissible: false,
  size: 'sm',
  hideHeader: true,
  className: 'card-select-modal modal-floating-panel',
  overlayClassName: 'card-select-modal-overlay',
});
